import { formErrorsFromFields, type FormErrors } from '@/lib/form-errors';
import type { ProductVariantPhotos } from '@/types/products';
import {
  IMAGE_FORMATS_LABEL,
  IMAGE_MIME_TYPES,
  PRODUCT_IMAGE_MAX_BYTES,
  validateImageFile,
  type ImageMimeType,
} from '@/lib/image-file';
import {
  MAX_COMBINACIONES,
  cleanStringList,
  isPriceTierRowEmpty,
  parseIntegerField,
  parsePriceField,
  type FaqForm,
  type OrderVariantForm,
  type PriceTierRow,
  type ProductFormState,
} from './products-payload';

/**
 * Validación en cliente del formulario de producto (módulo 3).
 *
 * Espejo de `backend/src/modules/products/products.schemas.ts`: los topes son los mismos que
 * aplica el backend, para que el usuario vea el error al instante en vez de después de un viaje
 * al servidor. El backend sigue validando: esto es comodidad, no la única barrera.
 *
 * Dos validaciones NO están en el schema (son reglas de captura de la pantalla) porque el bot
 * solo puede obedecer una cosa por rango:
 *  - la tabla de precios no puede tener dos rangos con la misma cantidad;
 *  - un rango empezado tiene que estar completo (cantidad y precio).
 */

/** Topes del contrato (`products.schemas.ts`), en un solo sitio para poder citarlos en la UI. */
export const PRODUCT_LIMITS = {
  nombre: 150,
  descripcion: 3000,
  fotos: 20,
  foto: 2000,
  variantes: 20,
  varianteNombre: 80,
  opciones: 100,
  opcion: 120,
  reglas: 50,
  regla: 500,
  faqs: 100,
  pregunta: 300,
  respuesta: 2000,
  combinaciones: 500,
  metaAdIds: 200,
  metaAdId: 200,
} as const;

function tooLong(value: string, max: number, label: string): string | undefined {
  return value.trim().length > max ? `${label} no puede superar los ${max} caracteres` : undefined;
}

/** `nombre` — obligatorio y hasta 150 caracteres (mismo mensaje que el backend). */
export function validateProductName(value: string): string | undefined {
  if (value.trim().length === 0) return 'El nombre es obligatorio';
  return tooLong(value, PRODUCT_LIMITS.nombre, 'El nombre');
}

/** `descripcion` — opcional, hasta 3000 caracteres (`null` para vaciarla al editar). */
export function validateDescription(value: string): string | undefined {
  return tooLong(value, PRODUCT_LIMITS.descripcion, 'La descripción');
}

/** `inventario` — vacío = sin control; si hay valor, entero ≥ 0. */
export function validateInventory(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return undefined;

  const parsed = parseIntegerField(text);
  if (parsed === null) return 'El inventario debe ser un número entero';
  if (parsed < 0) return 'El inventario no puede ser un número negativo';
  return undefined;
}

/**
 * `inventarioVariantes` — cada combinación con cantidad es un entero ≥ 0 (21-sep-2026).
 *
 * Vacío = esa combinación queda SIN control de inventario (no se guarda), igual que el campo general.
 * También se comprueba el tope de combinaciones: por encima del límite la pantalla corta la tabla y
 * el usuario tiene que reducir variantes.
 */
export function validateVariantInventory(value: Record<string, string>): string | undefined {
  for (const cantidad of Object.values(value)) {
    const text = (cantidad ?? '').trim();
    if (text.length === 0) continue;

    const parsed = parseIntegerField(text);
    if (parsed === null) return 'Cada cantidad del inventario por variante debe ser un número entero';
    if (parsed < 0) return 'Las cantidades del inventario por variante no pueden ser negativas';
  }

  const conCantidad = Object.values(value).filter((cantidad) => (cantidad ?? '').trim().length > 0);
  if (conCantidad.length > MAX_COMBINACIONES) {
    return `No se pueden guardar más de ${MAX_COMBINACIONES} combinaciones con cantidad`;
  }

  return undefined;
}

/** `tablaPrecios` — al menos un rango, cantidades enteras y únicas, precios ≥ 0. */
export function validatePriceTiers(rows: PriceTierRow[]): string | undefined {
  const filled = rows.filter((row) => !isPriceTierRowEmpty(row));
  if (filled.length === 0) {
    return 'La tabla de precios debe tener al menos un rango de cantidad';
  }

  const cantidades = new Set<number>();

  for (const row of filled) {
    const cantidad = parseIntegerField(row.cantidad);
    if (cantidad === null || cantidad < 1) return 'La cantidad debe ser un entero positivo';

    const precio = parsePriceField(row.precio);
    if (precio === null) return 'Completa el precio de cada rango (solo números)';
    if (precio < 0) return 'El precio no puede ser negativo';

    if (cantidades.has(cantidad)) return 'Hay dos rangos con la misma cantidad: deja solo uno';
    cantidades.add(cantidad);
  }

  return undefined;
}

/**
 * `fotos` — hasta 20 direcciones de hasta 2000 caracteres.
 *
 * Desde que el catálogo acepta subir fotos, lo normal es que cada entrada sea una URL del storage
 * local (`/uploads/productos/...` si no hay `PUBLIC_BASE_URL`, o absoluta si la hay), pero el campo
 * sigue siendo un `string[]`: aquí no se valida que sea una URL (el contrato tampoco lo exige).
 */
export function validateFotos(values: string[]): string | undefined {
  const items = cleanStringList(values);
  if (items.length > PRODUCT_LIMITS.fotos) {
    return `Puedes guardar hasta ${PRODUCT_LIMITS.fotos} fotos`;
  }
  if (items.some((item) => item.length > PRODUCT_LIMITS.foto)) {
    return `Cada dirección de foto puede tener hasta ${PRODUCT_LIMITS.foto} caracteres`;
  }
  return undefined;
}

/**
 * `fotosVariantes` — fotos por variante (21-sep-2026): una fila por valor (una por color).
 *
 * Cada fila con al menos una foto, hasta `PRODUCT_LIMITS.fotos` por fila y hasta `MAX_COMBINACIONES`
 * filas (el mismo tope que el inventario por combinación). Las filas sin fotos no se guardan
 * (`fotosVariantesFromForm` las descarta), así que aquí solo se comprueban los topes.
 */
export function validateVariantPhotos(rows: ProductVariantPhotos[]): string | undefined {
  const grupos = rows.filter((fila) => cleanStringList(fila.fotos).length > 0);

  if (grupos.length > MAX_COMBINACIONES) {
    return `No se pueden guardar fotos de más de ${MAX_COMBINACIONES} valores de variante`;
  }

  for (const fila of grupos) {
    const fotos = cleanStringList(fila.fotos);

    if (fotos.length > PRODUCT_LIMITS.fotos) {
      return `Cada valor de variante puede tener hasta ${PRODUCT_LIMITS.fotos} fotos`;
    }
    if (fotos.some((foto) => foto.length > PRODUCT_LIMITS.foto)) {
      return `Cada dirección de foto puede tener hasta ${PRODUCT_LIMITS.foto} caracteres`;
    }
  }

  return undefined;
}

/**
 * Topes de la subida de fotos del catálogo: NO son los mismos que los del logo (Ajustes → General) ni
 * los de la guía de envío. El catálogo acepta hasta 20 fotos POR PRODUCTO, así que tiene su propio
 * tope más bajo (`PRODUCT_IMAGE_MAX_BYTES`, 2 MB, espejo de `MAX_PRODUCT_IMAGE_BYTES` en
 * `backend/src/lib/local-storage.ts`) y el panel reescala la foto antes de subirla
 * (`prepararFotoProducto`). La regla de tamaños vive en `lib/image-file.ts` para no repetirla.
 */
export const PRODUCT_PHOTO_MAX_BYTES = PRODUCT_IMAGE_MAX_BYTES;
export const PRODUCT_PHOTO_FORMATS_LABEL = IMAGE_FORMATS_LABEL;
export const PRODUCT_PHOTO_MIME_TYPES = IMAGE_MIME_TYPES;

export type ProductPhotoMimeType = ImageMimeType;

/**
 * `archivo` — la foto elegida en el equipo, ANTES de leerla (así no se lee un archivo de 20 MB para
 * que el backend lo rechace después). Solo mira tamaño y MIME declarado; el formato real lo confirma
 * el backend por firma binaria.
 */
export function validatePhotoFile(file: {
  name: string;
  size: number;
  type: string;
}): string | undefined {
  return validateImageFile(file, PRODUCT_PHOTO_MAX_BYTES);
}

/** `fotos` — el cupo que queda: la pantalla avisa antes de subir y evita llenar la lista de 20. */
export function validatePhotoCount(current: number, adding: number): string | undefined {
  if (current + adding > PRODUCT_LIMITS.fotos) {
    return `Puedes guardar hasta ${PRODUCT_LIMITS.fotos} fotos`;
  }
  return undefined;
}

/** `reglas` — hasta 50 reglas de hasta 500 caracteres. */
export function validateReglas(values: string[]): string | undefined {
  const items = cleanStringList(values);
  if (items.length > PRODUCT_LIMITS.reglas) {
    return `Puedes definir hasta ${PRODUCT_LIMITS.reglas} reglas`;
  }
  if (items.some((item) => item.length > PRODUCT_LIMITS.regla)) {
    return `Cada regla puede tener hasta ${PRODUCT_LIMITS.regla} caracteres`;
  }
  return undefined;
}

/** `metaAdIds` — hasta 200 IDs de hasta 200 caracteres. */
export function validateMetaAdIds(values: string[]): string | undefined {
  const items = cleanStringList(values);
  if (items.length > PRODUCT_LIMITS.metaAdIds) {
    return `Puedes vincular hasta ${PRODUCT_LIMITS.metaAdIds} anuncios`;
  }
  if (items.some((item) => item.length > PRODUCT_LIMITS.metaAdId)) {
    return `Cada ID de anuncio puede tener hasta ${PRODUCT_LIMITS.metaAdId} caracteres`;
  }
  return undefined;
}

/** `datosParaOrden` — hasta 20 variantes; cada una con nombre y opciones dentro de los topes. */
export function validateOrderVariants(rows: OrderVariantForm[]): string | undefined {
  const filled = rows.filter(
    (row) => row.nombre.trim().length > 0 || cleanStringList(row.opciones).length > 0,
  );

  if (filled.length > PRODUCT_LIMITS.variantes) {
    return `Puedes definir hasta ${PRODUCT_LIMITS.variantes} variantes para la orden`;
  }

  for (const row of filled) {
    const nombre = row.nombre.trim();
    const opciones = cleanStringList(row.opciones);

    if (nombre.length === 0) return 'Cada variante necesita su nombre (ej. «Color»)';
    if (nombre.length > PRODUCT_LIMITS.varianteNombre) {
      return `El nombre de la variante no puede superar los ${PRODUCT_LIMITS.varianteNombre} caracteres`;
    }
    if (opciones.length > PRODUCT_LIMITS.opciones) {
      return `Cada variante puede tener hasta ${PRODUCT_LIMITS.opciones} opciones`;
    }
    if (opciones.some((opcion) => opcion.length > PRODUCT_LIMITS.opcion)) {
      return `Cada opción puede tener hasta ${PRODUCT_LIMITS.opcion} caracteres`;
    }
  }

  return undefined;
}

/** `faqs` — hasta 100 preguntas de 300 caracteres con respuesta de hasta 2000. */
export function validateFaqs(rows: FaqForm[]): string | undefined {
  const filled = rows.filter(
    (row) => row.pregunta.trim().length > 0 || row.respuesta.trim().length > 0,
  );

  if (filled.length > PRODUCT_LIMITS.faqs) {
    return `Puedes definir hasta ${PRODUCT_LIMITS.faqs} preguntas frecuentes`;
  }

  for (const row of filled) {
    const pregunta = row.pregunta.trim();
    const respuesta = row.respuesta.trim();

    if (pregunta.length === 0) return 'Cada pregunta frecuente necesita su enunciado';
    if (respuesta.length === 0) return 'Cada pregunta frecuente necesita su respuesta';
    if (pregunta.length > PRODUCT_LIMITS.pregunta) {
      return `La pregunta no puede superar los ${PRODUCT_LIMITS.pregunta} caracteres`;
    }
    if (respuesta.length > PRODUCT_LIMITS.respuesta) {
      return `La respuesta no puede superar los ${PRODUCT_LIMITS.respuesta} caracteres`;
    }
  }

  return undefined;
}

/** Validación completa del formulario, con la forma que consumen `Field` y `FormErrorAlert`. */
export function validateProductForm(form: ProductFormState): FormErrors {
  return formErrorsFromFields({
    nombre: validateProductName(form.nombre),
    descripcion: validateDescription(form.descripcion),
    inventario: validateInventory(form.inventario),
    inventarioVariantes: validateVariantInventory(form.inventarioVariantes),
    fotosVariantes: validateVariantPhotos(form.fotosVariantes),
    fotos: validateFotos(form.fotos),
    tablaPrecios: validatePriceTiers(form.tablaPrecios),
    datosParaOrden: validateOrderVariants(form.datosParaOrden),
    reglas: validateReglas(form.reglas),
    faqs: validateFaqs(form.faqs),
    metaAdIds: validateMetaAdIds(form.metaAdIds),
  });
}
